/**
 * Read-only viewer for the template's extracted docxtemplater tags ("body
 * tags" — the historical name; extraction covers every word/*.xml text part:
 * body, headers, footers, footnotes). Pairs matching
 * open/close control tags and highlights orphaned tags with warnings. The
 * orphan-pairing rule itself lives in `countOrphanControlTags`
 * (`frontend/src/utils/templateHealth.ts`) — the single source shared with
 * the Template Health hub — this component only re-derives the display
 * grouping (open/close/inverted per base name) that its rendering needs.
 * Uses brand-compliant colours (brand-* instead of blue/purple, amber instead of yellow).
 */

import { AlertTriangle, Tag } from 'lucide-react';
import { countOrphanControlTags } from '../../utils/templateHealth';

interface PairedTag {
  name: string;
  hasOpen: boolean;
  hasClose: boolean;
  hasInverted: boolean;
  isOrphaned: boolean;
}

interface BodyTagsViewerProps {
  bodyTags: string[];
}

function pairControlTags(tags: string[]): PairedTag[] {
  const controlTags = tags.filter((t) => t.startsWith('#') || t.startsWith('/') || t.startsWith('^'));
  const grouped: Record<string, { hasOpen: boolean; hasClose: boolean; hasInverted: boolean }> = {};

  for (const tag of controlTags) {
    const prefix = tag[0];
    const baseName = tag.slice(1);
    if (!grouped[baseName]) {
      grouped[baseName] = { hasOpen: false, hasClose: false, hasInverted: false };
    }
    if (prefix === '#') grouped[baseName].hasOpen = true;
    if (prefix === '/') grouped[baseName].hasClose = true;
    if (prefix === '^') grouped[baseName].hasInverted = true;
  }

  return Object.entries(grouped).map(([name, g]) => ({
    name,
    ...g,
    isOrphaned: !(g.hasOpen && g.hasClose),
  }));
}

export default function BodyTagsViewer({ bodyTags }: BodyTagsViewerProps) {
  if (bodyTags.length === 0) {
    return (
      <p className="text-body-sm text-slate-500 dark:text-slate-400">
        No body tags found. Upload a template version to extract tags.
      </p>
    );
  }

  const simpleTags = bodyTags.filter((t) => !t.startsWith('#') && !t.startsWith('/') && !t.startsWith('^'));
  const pairedTags = pairControlTags(bodyTags);
  const hasOrphans = countOrphanControlTags(bodyTags) > 0;

  return (
    <div className="space-y-4">
      {simpleTags.length > 0 && (
        <div>
          <h4 className="text-body-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Variable Tags ({simpleTags.length})</h4>
          <div className="flex flex-wrap gap-2">
            {simpleTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-1 rounded-md text-caption font-mono bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-800"
              >
                <Tag className="h-3 w-3 mr-1" />
                {`{${tag}}`}
              </span>
            ))}
          </div>
        </div>
      )}

      {pairedTags.length > 0 && (
        <div>
          <h4 className="text-body-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Control Tags ({pairedTags.length})</h4>
          <div className="flex flex-wrap gap-2">
            {pairedTags.map((tag) =>
              tag.isOrphaned ? (
                <span
                  key={tag.name}
                  data-testid="orphan-tag"
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-caption font-mono bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {tag.hasOpen && !tag.hasClose && `{#${tag.name}}`}
                  {!tag.hasOpen && tag.hasClose && `{/${tag.name}}`}
                  {tag.hasInverted && !tag.hasOpen && !tag.hasClose && `{^${tag.name}}`}
                </span>
              ) : (
                <span
                  key={tag.name}
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-caption font-mono bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-800"
                >
                  {`{#${tag.name}} ... {/${tag.name}}`}
                </span>
              )
            )}
          </div>
        </div>
      )}

      {hasOrphans && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-body-sm text-amber-700 dark:text-amber-400">
            Some control tags are orphaned — they have an opening tag without a matching closing tag, or vice versa.
          </p>
        </div>
      )}
    </div>
  );
}
